import { Flex, Heading } from "@chakra-ui/react";
import { InputControl } from "formik-chakra-ui";
import { useCallback, useMemo } from "react";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import * as OrgService from "src/services/orgService";
import * as RD from "src/types/remoteData";
import { z } from "zod";

export const Form = () => {
  return (
    <Flex direction="column" gap={2}>
      <Heading as="h3" variant="admin">
        Account lookup
      </Heading>
      <FormikSwitchableContent<"enabled" | "disabled">
        label="Enable account lookup"
        description=""
        name="accountLookup.status"
        checked={["enabled", ""]}
        unchecked={["disabled", ""]}
        showContent={(value) => value === "enabled"}
      >
        <InputControl
          name={`accountLookup.supportUrl`}
          label="Support URL"
          inputProps={{ placeholder: "Support URL" }}
          isRequired
        />
      </FormikSwitchableContent>
    </Flex>
  );
};

export function useForm(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const { remoteData, update } =
    OrgService.useOrgConfig<OrgConfig.AccountLookupConfig>("AccountLookup");
  const initialValues = useMemo(() => {
    return remoteData.map((config): FormikType => {
      if (config?.disabled === false) {
        return {
          accountLookup: {
            status: "enabled",
            supportUrl: config.supportUrl ?? "",
          },
        };
      }

      return {
        accountLookup: {
          status: "disabled",
          supportUrl: "",
        },
      };
    });
  }, [remoteData]);

  const submit = useCallback(
    async (values: FormikType) => {
      if (values.accountLookup.status === "enabled") {
        await update({
          disabled: false,
          supportUrl: values.accountLookup.supportUrl,
        });
      } else {
        await update({
          disabled: true,
          supportUrl: values.accountLookup.supportUrl ?? "",
        });
      }
    },
    [update]
  );

  return {
    initialValues,
    submit,
  };
}

export const FormikSchema = z.object({
  accountLookup: z.discriminatedUnion("status", [
    z.object({
      status: z.literal("enabled"),
      supportUrl: z.string().url(),
    }),
    z.object({
      status: z.literal("disabled"),
      supportUrl: z.string(),
    }),
  ]),
});

type FormikType = z.infer<typeof FormikSchema>;
